import React from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2 } from "lucide-react";

/**
 * Bulk-selection bar. Renders a "X selected" indicator + Delete selected button
 * when at least one item is selected. Also exposes a "select all" checkbox.
 *
 * Props:
 *  - selected (string[])
 *  - allIds (string[])
 *  - onChangeSelected (fn)
 *  - onBulkDelete (fn)  — receives selected ids
 *  - testId (string)
 */
export function BulkBar({ selected, allIds, onChangeSelected, onBulkDelete, testId = "bulk-bar" }) {
  if (selected.length === 0) return null;
  return (
    <div className="flex items-center justify-between border border-primary/40 bg-primary/5 rounded-sm px-4 py-2.5" data-testid={testId}>
      <div className="text-sm font-medium">
        <span className="mono-num">{selected.length}</span> of <span className="mono-num">{allIds.length}</span> selected
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => onChangeSelected([])} className="rounded-sm">Clear</Button>
        <Button size="sm" variant="destructive" onClick={() => onBulkDelete(selected)} className="rounded-sm" data-testid={`${testId}-delete`}>
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete selected
        </Button>
      </div>
    </div>
  );
}

export function SelectAllCheckbox({ selected, allIds, onChange, testId = "select-all" }) {
  const checked = allIds.length > 0 && selected.length === allIds.length;
  const indeterminate = selected.length > 0 && selected.length < allIds.length;
  return (
    <Checkbox
      checked={indeterminate ? "indeterminate" : checked}
      onCheckedChange={(v) => onChange(v ? allIds : [])}
      data-testid={testId}
      aria-label="Select all"
    />
  );
}

export function RowCheckbox({ id, selected, onChange, testId }) {
  const isChecked = selected.includes(id);
  return (
    <Checkbox
      checked={isChecked}
      onCheckedChange={(v) => onChange(v ? [...selected, id] : selected.filter((x) => x !== id))}
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
      aria-label="Select row"
    />
  );
}
